import {
  useCallback,
  useState,
} from 'react';
import { BusinessModel } from '../../interfaces/BusinessModel.interface';
import createProfile from '../../services/intake/createProfile';
import linkSocials from '../../services/intake/linkSocials';

const MAX_POLL_DURATION = 10000;

const pollLinkSocials = async (
  business: BusinessModel, redirectUrl: string, duration: number = 0,
): Promise<{ url: string }> => {
  if (duration > MAX_POLL_DURATION) throw new Error('Unable to get Social Linking URL in a reasonable time.');
  const now = Date.now();
  try {
    return await linkSocials(business, redirectUrl);
  } catch (err) {
    // @ts-ignore
    if (err instanceof Error && err.response && err.response.status === 400) {
      await new Promise(res => setTimeout(res, 1000));
      const newDuration = duration + (Date.now() - now);
      return pollLinkSocials(business, redirectUrl, newDuration);
    }
    throw err;
  }
};

const usePrepareSocialLinking = (): [
  (business: BusinessModel, redirectUrl: string) => Promise<string | undefined>,
  boolean,
  Error?,
] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const prepareSocialLinking = useCallback(
    // eslint-disable-next-line consistent-return
    async (business: BusinessModel, redirectUrl: string): Promise<string | undefined> => {
      setError(undefined);
      setLoading(true);
      try {
        await createProfile(business);

        const response = await pollLinkSocials(business, redirectUrl);

        return response.url;
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return [prepareSocialLinking, loading, error];
};

export default usePrepareSocialLinking;
